"use client";

import { useCallback, useEffect, useMemo } from "react";
import { usePostHog } from "posthog-js/react";

import { getRequiredFieldsForSlug } from "../validation/validateSignUpField";
import { SignupFlowSlug } from "../constants";
import { resolveFeatureFlagVariant } from "../featureFlags";
import type { TCatFieldName } from "@./suf-store";

import { useSignupFlowStore } from "./useSignUpFlowStore";
import { useSignupFlowErrorStore } from "./useSignUpFlowErrorStore";

export const usePageStatus = (slug: SignupFlowSlug) => {
  const validatedForSlug = useSignupFlowErrorStore(
    (state) => state.validatedFields[slug] || [],
  );

  const validated = useMemo(() => new Set(validatedForSlug), [ validatedForSlug ]);

  const {
    activeCatId,
    cats,
    activeCatIds,
    activeCatCount,
    featureFlags,
    migrateCatData,
    revertMigrateCatData,
  } = useSignupFlowStore((state) => ({
    activeCatId: state.activeCatId,
    cats: state.cats,
    activeCatIds: state.getActiveCatIds(),
    activeCatCount: state.getActiveCatIds().length,
    featureFlags: state.featureFlags,
    migrateCatData: state.migrateCatData,
    revertMigrateCatData: state.revertMigrateCatData,
  }));

  const posthog = usePostHog();
  const inABTestGroup = useCallback(
    (flag: string): boolean => resolveFeatureFlagVariant(
      flag,
      featureFlags,
      () => posthog?.getFeatureFlag(flag),
      activeCatCount,
    ) === "test",
    [ activeCatCount, featureFlags, posthog ],
  );

  const requiredFields = useMemo(
    () => getRequiredFieldsForSlug(slug, { inABTestGroup }),
    [ slug, cats, inABTestGroup ],
  );

  const getFieldNamesForCat = (catId: string): TCatFieldName[] => requiredFields
    .filter((field) => field.startsWith(`cat[${catId}].`))
    .map((field) => field.replace(`cat[${catId}].`, "") as TCatFieldName);

  useEffect(() => {
    const filtered = validatedForSlug.filter((field) => requiredFields.includes(field));

    if (filtered.length !== validatedForSlug.length) {
      useSignupFlowErrorStore.setState((state) => ({
        validatedFields: {
          ...state.validatedFields,
          [slug]: filtered,
        },
      }));
    }
  }, [ slug, validatedForSlug, requiredFields ]);

  const isCatComplete = (catId: string): boolean => requiredFields
    .filter((key) => key.startsWith(`cat[${catId}]`))
    .every((fieldKey) => validated.has(fieldKey));

  const catCompletionMap: Record<string, boolean> = {};
  for (const catId of activeCatIds) {
    catCompletionMap[catId] = isCatComplete(catId);
  }

  const blankCatIds = activeCatIds.filter((catId) => !validatedForSlug.some((field) => field.startsWith(`cat[${catId}].`)));

  const isPageComplete = requiredFields.every((fieldKey) => validated.has(fieldKey));

  /**
 * Migrates both cat data and validation fields from a source cat to one or more target cats.
 *
 * This is used when the user chooses to copy information (like gender or breed) from one cat
 * to others, so both the form data and its validation state are replicated.
 *
 * @param fieldNames - The list of field names to migrate (e.g. ["gender", "breed"]).
 * @param sourceId - The ID of the source cat (e.g. "cat-1").
 * @param targetIds - The IDs of the target cats (e.g. ["cat-2", "cat-3"]).
 *
 * @example
 * migrateCatDataAndValidation(["gender", "breed"], "cat-1", ["cat-2"]);
 * // - Copies field values from cat-1 to cat-2
 * // - Copies validation state for the migrated fields
 */
  const migrateCatDataAndValidation = (
    fieldNames: TCatFieldName[],
    sourceId: string,
    targetIds: string[],
  ) => {
    migrateCatData(fieldNames, sourceId, targetIds);
    migrateValidatedFields(fieldNames, sourceId, targetIds);
  };

  const migrateValidatedFields = (
    fieldNames: TCatFieldName[],
    sourceId: string,
    targetIds: string[],
  ) => {
    const sourceValidatedFields = validatedForSlug.filter((fieldKey) => {
      if (!fieldKey.startsWith(`cat[${sourceId}].`)) {
        return false;
      }

      const fieldName = fieldKey.replace(`cat[${sourceId}].`, "");
      return fieldNames.includes(fieldName as TCatFieldName);
    });

    const newValidatedFields: string[] = [];

    for (const targetId of targetIds) {
      for (const sourceField of sourceValidatedFields) {
        const targetField = sourceField.replace(
          `cat[${sourceId}]`,
          `cat[${targetId}]`,
        );
        newValidatedFields.push(targetField);
      }
    }

    const updatedValidatedFields = [ ...validatedForSlug, ...newValidatedFields ];

    useSignupFlowErrorStore.setState((state) => ({
      validatedFields: {
        ...state.validatedFields,
        [slug]: updatedValidatedFields,
      },
    }));
  };

  /**
 * Reverses a previous data migration by removing both cat data and validation fields
 * that were copied from one cat to others.
 *
 * This is useful when users uncheck a "copy to other cats" checkbox,
 * and we want to cleanly remove the previously duplicated fields.
 *
* @param fieldNames - The list of field names to remove (e.g. ["gender", "breed"]) (`TCatFieldName[]`)
 * @param targetIds - The IDs of the target cats to remove data and validation from (e.g. ["cat-2", "cat-3"]).
 *
 * @example
 * unmigrateCatDataAndValidation(["gender", "breed"], ["cat-2"]);
 * // Removes both form data and validation fields for "cat-2" that were previously copied
 */
  const revertMigrateCatDataAndValidation = (
    fieldNames: TCatFieldName[],
    targetIds: string[],
  ) => {
    revertMigrateCatData(fieldNames, targetIds);
    revertMigrateValidatedFields(targetIds);
  };

  const revertMigrateValidatedFields = (targetIds: string[]) => {
    const updatedValidatedFields = validatedForSlug.filter((fieldKey) => !targetIds.some((targetId) => fieldKey.startsWith(`cat[${targetId}].`)));

    useSignupFlowErrorStore.setState((state) => ({
      validatedFields: {
        ...state.validatedFields,
        [slug]: updatedValidatedFields,
      },
    }));
  };

  return {
    isPageComplete,
    isCatComplete: activeCatId ? isCatComplete(activeCatId) : false,
    catCompletionMap,
    blankCatIds,
    migrateCatDataAndValidation,
    revertMigrateCatDataAndValidation,
    getFieldNamesForCat,
  };
};
